import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, CircularProgress } from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import styled from '@emotion/styled';
import { Button } from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { setContractReference, setStripeConfigType, setSubscriptionId } from '../rcSlice';
import { subscriptionRcApi, attestationRcContract } from '@/api/rcApi';
import { appError, appLoading, appSuccess } from '@/redux/actions/appActions';
import { getDocument } from '@/redux/actions/contractsActions';
import { trackDatadogError } from '@/services/datadog';
import { formatDateOnly } from '@/utils/date';
import ErrorBoundary from '../../../components/ErrorBoundary/ErrorBoundary';

const ButtonStyled = styled(Button)`
  background-color: #2f6975;
  color: white;
  border-radius: 30px;
  width: 100%;
  height: 40px;
  font-weight: 600;
  font-size: 15px;
  margin-top: 16px;
  &:hover {
    background-color: #26555f;
  }
`;

interface Props {
  startDate: string;
  endDate: string;
}

const CheckoutForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation('common');
  const [loading, setLoading] = useState(false);
  // `elements` existe dès que <Elements> est monté ; il ne dit rien du montage
  // effectif de l'iframe. Sans cette garde, un clic sur Payer trop tôt part en
  // IntegrationError « elements should have a mounted Payment Element ».
  const [ready, setReady] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setErrorMessage('');

    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/insurance/rc/success-payment`,
        },
      });

      if (result.error) {
        setErrorMessage(result.error.message || t('payment.start-failed'));
        trackDatadogError(
          new Error(result.error.message || 'Stripe confirmPayment failed'),
          {
            action: 'rc_stripe_confirm_payment_failed',
            stripeErrorType: result.error.type,
            stripeErrorCode: result.error.code,
          },
        );
      }
    } catch (err) {
      // confirmPayment LÈVE ses IntegrationError au lieu de les retourner dans
      // result.error. Sans ce catch, la promesse rejette : setLoading(false)
      // n'est jamais atteint et le bouton reste bloqué en spinner.
      setErrorMessage(t('payment.start-failed'));
      trackDatadogError(err as Error, {
        action: 'rc_stripe_confirm_payment_threw',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* link: 'never' désactive le wallet Stripe Link (friction email/tel inutile,
          l'email est déjà attaché au customer côté back). Cast nécessaire car les
          types de @stripe/stripe-js@1.17 ne déclarent pas encore `link` dans
          PaymentWalletsOption, mais le runtime Stripe.js (CDN, à jour) le supporte. */}
      <PaymentElement
        options={{ wallets: { link: 'never' } as never }}
        onReady={() => setReady(true)}
      />
      {errorMessage && (
        <Typography sx={{ mt: 1, color: 'error.main', fontSize: 13 }}>
          {errorMessage}
        </Typography>
      )}
      <ButtonStyled type="submit" disabled={!stripe || !ready || loading}>
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Payer'}
      </ButtonStyled>
    </form>
  );
};

interface ExistingContract {
  contractReference: string;
  endDate: string;
  message: string;
}

const RcPaymentForm: React.FC<Props> = ({ startDate, endDate }) => {
  const dispatch = useAppDispatch();
  const contractReference = useAppSelector(state => state.rc.contractReference);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripePromise, setStripePromise] = useState<any>(null);
  const [existingContract, setExistingContract] = useState<ExistingContract | null>(null);
  const [attestationId, setAttestationId] = useState<number | null>(null);
  const [forceSubscribe, setForceSubscribe] = useState(false);

  useEffect(() => {
    if (existingContract && !forceSubscribe) return;

    const createSession = async () => {
      try {
        dispatch(appLoading());
        const response = await subscriptionRcApi({ startDate, endDate, contractReference, forceSubscription: forceSubscribe });
        const { clientSecret: secret, publicKey, contractReference: ref, stripeConfigType, subscriptionId } = response.data;

        setClientSecret(secret);
        setStripePromise(loadStripe(publicKey));
        dispatch(setContractReference(ref));
        dispatch(setStripeConfigType(stripeConfigType));
        dispatch(setSubscriptionId(subscriptionId));

        // Persist contractReference for Stripe redirect recovery
        localStorage.setItem('rc_contractReference', ref);

        dispatch(appSuccess());
      } catch (err: any) {
        const errorData = err?.response?.data;
        if (errorData?.error === 'existing_rc_contract') {
          setExistingContract({
            contractReference: errorData.contractReference,
            endDate: errorData.endDate,
            message: errorData.message,
          });
          // Fetch attestation for the existing contract
          attestationRcContract(errorData.contractReference)
            .then(res => {
              if (res.data?.attestationId) {
                setAttestationId(res.data.attestationId);
              }
            })
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            .catch(() => {});
          dispatch(appSuccess());
        } else {
          console.error('Error creating RC session:', err);
          dispatch(appError());
        }
      }
    };

    createSession();
  }, [startDate, endDate, dispatch, forceSubscribe]);

  const handleDownloadAttestation = () => {
    if (attestationId) {
      dispatch(getDocument({ id: attestationId, filename: 'attestation-rc.pdf' }));
    }
  };

  const handleForceSubscribe = () => {
    setExistingContract(null);
    setForceSubscribe(true);
  };

  // Show existing contract warning
  if (existingContract && !forceSubscribe) {
    const endDateFormatted = formatDateOnly(existingContract.endDate);
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom sx={{ color: '#e65100' }}>
          Contrat RC déjà actif
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {existingContract.message}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Valide jusqu&apos;au {endDateFormatted}
        </Typography>

        {attestationId && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              background: '#e3f2fd',
              borderRadius: 2,
              px: 3,
              py: 2,
              mb: 3,
              boxShadow: 1,
              cursor: 'pointer',
            }}
            onClick={handleDownloadAttestation}
          >
            <InsertDriveFileIcon color="primary" sx={{ fontSize: 28 }} />
            <Typography sx={{ fontWeight: 600, color: '#1976d2', fontSize: 13 }}>
              Télécharger mon attestation
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <ButtonStyled onClick={handleForceSubscribe}>
            Souscrire quand même
          </ButtonStyled>
        </Box>
      </Box>
    );
  }

  if (!clientSecret || !stripePromise) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom textAlign="center">
        Paiement sécurisé
      </Typography>
      <ErrorBoundary variant="payment">
        <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret }}>
          <CheckoutForm />
        </Elements>
      </ErrorBoundary>
    </Box>
  );
};

export default RcPaymentForm;
